import { type FC, useCallback } from 'react';

import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import cn from 'classnames';

import { tr } from '../../../constants';
import type { ImageOptions } from '../Image';
import { isUploadingImageSrc } from '../isUploadingImageSrc';

export const ImageView: FC<NodeViewProps> = ({ node, editor, extension, getPos, deleteNode }) => {
  const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
  const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
  const isUploading = isUploadingImageSrc(src);
  const { translations } = extension.options as ImageOptions;

  const handleDelete = useCallback(() => {
    if (typeof deleteNode === 'function') {
      deleteNode();
      return;
    }
    if (typeof getPos !== 'function') return;
    const pos = getPos();
    if (typeof pos !== 'number') return;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  }, [deleteNode, editor, getPos, node.nodeSize]);

  return (
    <NodeViewWrapper as="div" className="community-editor-image-wrapper" data-drag-handle contentEditable={false}>
      <div className="relative inline-block size-[120px] overflow-hidden rounded-xl border border-black/10 bg-black/5">
        <img
          src={src}
          alt={alt}
          className={cn('size-full object-cover', { 'opacity-50': isUploading })}
          draggable={false}
        />
        {isUploading && (
          <span
            role="status"
            aria-label={tr(translations, 'community.composer.upload_loading')}
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="size-5 animate-spin rounded-full border-2 border-black/20 border-t-black/60" />
          </span>
        )}
        {editor.isEditable && (
          <button
            type="button"
            onClick={handleDelete}
            aria-label="Remove image"
            className="absolute right-1 top-1 inline-flex size-6 items-center justify-center rounded-full bg-black/70 text-white shadow-sm transition-colors hover:bg-black"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    </NodeViewWrapper>
  );
};
